import { getIpCountry } from '@/api/public';
import logger from '@/utils/client-logger';
import { EU_REGION } from '@/utils/constants';
import { env } from '@/utils/env';
import { getCookie } from '@/utils/tools';
import { Provider, ProviderType, TrackingEvent } from '../types';

interface Ga4Config {
  measurementId: string;
  adId1?: string;
  adId2?: string;
  isEnabled?: boolean;
}

export class Ga4Provider implements Provider {
  type = ProviderType.GA4;
  isEnabled: boolean;
  private initialized = false;
  private config: Ga4Config;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  constructor(config: Ga4Config) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  /** 等待 GA4 script 真正 ready 后返回初始化结果。 */
  async init(): Promise<boolean> {
    /** 读取并缓存 GA clientId，SDK 尚未暴露时继续轮询。 */
    function setClientId() {
      let clientId = localStorage.getItem('zl_ga_clientId');
      if (!clientId) {
        if (window.ga && window.ga.getAll) {
          clientId = window.ga.getAll()[0].get('clientId');
          localStorage.setItem('zl_ga_clientId', clientId);
        } else {
          // ga.getAll 是由gatg 的 js逻辑触发加载的，无法监控onload事件，所以这里轮询判断
          setTimeout(() => {
            setClientId();
          }, 1000);
        }
      }
    }
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      return await new Promise<boolean>((resolve) => {
        let settled = false;
        let scriptCallbackHandled = false;
        /** 确保 script 成功或失败只完成一次初始化。 */
        const settle = (result: boolean): void => {
          if (settled) return;
          settled = true;
          resolve(result);
        };

        /** 仅允许 onload 或 onerror 中的首个 script 结果继续处理。 */
        const enterScriptCallback = (): boolean => {
          if (scriptCallbackHandled) return false;
          scriptCallbackHandled = true;
          return true;
        };

        try {
          // Get the instance if it's already loaded
          const f = document.getElementsByTagName('script')[0];
          const j = document.createElement('script');
          j.async = true;
          j.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.measurementId}`;
          j.setAttribute('data-cmp-ab', '1');
          j.onload = () => {
            if (!enterScriptCallback()) return;
            setTimeout(() => {
              void (async () => {
                if (settled) return;

                try {
                  setClientId();
                  const userId = localStorage.getItem('zl-userId') || '';
                  if (!window.dataLayer) {
                    window.dataLayer = [];
                  }
                  if (!window.gtag) {
                    window.gtag = function () {
                      // eslint-disable-next-line prefer-rest-params
                      window.dataLayer.push(arguments);
                    };
                  }
                  /* 谷歌意见征求模式v2 Google Consent Mode v2 此事件必须放在第一个顺序 */
                  try {
                    const { data } = await getIpCountry();
                    if (settled) return;
                    if (EU_REGION.includes(data?.country_id)) {
                      // 1. 每个页面先默认 不同意追踪 及 不接受 cookie
                      window.gtag('consent', 'default', {
                        analytics_storage: 'denied',
                        ad_storage: 'denied',
                        ad_user_data: 'denied',
                        ad_personalization: 'denied',
                        region: EU_REGION, // apply default to specific regions only
                      });
                      window.gtag({ event: 'default_consent' });

                      // 之前用户选择过了同意情况了，直接同步给ga
                      try {
                        const opt = getCookie('OptanonConsent');
                        if (opt) {
                          const optStr = decodeURIComponent(opt);
                          // C001 Strictly Necessary Cookies; C0002 Performance Cookies; C0003 Functional Cookies; C0004 Targeting Cookies
                          const canUseAnalysis = /C0002:(\d)/.exec(optStr)?.[1] === '1';
                          const canUseAd = /C0004:(\d)/.exec(optStr)?.[1] === '1';
                          // 只更新用户同意的情况，避免对美国用户产生影响
                          const sendData: any = {};
                          if (canUseAnalysis) {
                            sendData.analytics_storage = 'granted';
                          }
                          if (canUseAd) {
                            sendData.ad_storage = 'granted';
                            sendData.ad_user_data = 'granted';
                            sendData.ad_personalization = 'granted';
                          }
                          if (canUseAnalysis || canUseAd) {
                            window.gtag('consent', 'update', sendData);
                          }
                        }
                      } catch (error) {
                        logger.error(error);
                      }
                    } else {
                      window.gtag('consent', 'default', {
                        analytics_storage: 'granted',
                        ad_storage: 'granted',
                        ad_user_data: 'granted',
                        ad_personalization: 'granted',
                      });
                    }
                    // 保存ip
                    localStorage.setItem('zl-userIP', data?.ip);
                  } catch (error) {
                    logger.log(error);
                  }

                  if (settled) return;
                  // Google Analytics
                  window.gtag('js', new Date());
                  // Google Ads
                  if (this.config.adId1) {
                    window.gtag('config', this.config.adId1);
                  }
                  if (this.config.adId2) {
                    window.gtag('config', this.config.adId2, {
                      allow_enhanced_conversions: true,
                    });
                  }
                  window.gtag('config', env.NEXT_PUBLIC_GA4_MEASUREMENT_ID);
                  window.gtag('set', { user_id: userId });
                  this.initialized = true;
                  settle(true);
                } catch (error) {
                  logger.error('Failed to initialize Google Analytics:', error);
                  settle(false);
                }
              })();
            }, 0);
          };
          j.onerror = () => {
            if (!enterScriptCallback()) return;
            logger.error('Failed to load Google Analytics script');
            settle(false);
          };

          if (!f?.parentNode) {
            logger.error('Failed to find Google Analytics script insertion point');
            settle(false);
            return;
          }
          f.parentNode.insertBefore(j, f);
        } catch (error) {
          logger.error('Failed to initialize Google Analytics:', error);
          settle(false);
        }
      });
    } catch (error) {
      logger.error('Failed to initialize Google Analytics:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('Ga4 tracking event:', event);
      }
      // https://developers.google.com/tag-platform/gtagjs/configure?hl=zh-cn
      // 根据文档，Gtag事件发送有几种操作：
      // 1. 使用 config 命令在您的网站和 Google 产品之间建立数据流
      // 2. 使用 event 命令发送事件数据
      // 3. 使用 set 命令设置随每个事件发送的值

      const gtag = window.gtag;

      // 如果有 eventId，添加到 properties 中
      const properties = event.properties || {};
      if (event.eventId && !properties.event_id) {
        properties.event_id = event.eventId;
      }

      gtag(event.commandType || 'event', event.eventName, properties);
    } catch (error) {
      logger.error('Ga4 tracking error:', error);
    }
  }

  /**
   * 启用GA4日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用GA4日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
